
	import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
	import Modal from '@/components/Modal.vue';
	import SearchSVG from '@/components/SearchSVG.vue';
	import { Observer } from 'mobx-vue';
	import PatientRow from '@/components/patient-list/PatientRow.vue';
	import Loading from '@/components/Loading.vue';
	import { ProgressInfo } from '@/components/patient-list/PatientTable.vue';
	import { Order, Patient } from '@/store/engine/openapi/generated';
	import { OrderWithPatient } from '@/store/DoctorStore';
	import GlobalSearchRow from '@/components/patient-list/GlobalSearchRow.vue';

	function stringLiterals<T extends string>(...args: T[]): T[] {
		return args;
	}

	type ElementType<T extends ReadonlyArray<unknown>> = T extends ReadonlyArray<infer ElementType> ? ElementType : never;

	interface SearchByFieldParams {
		fetchFromPatient: (p: Patient) => string | undefined;
		fetchFromOrderWihtPatient: (op: OrderWithPatient) => string | undefined;
	}

	const types = stringLiterals('by patient identification', 'by reference ID', 'by order number', 'by PO');

	export type SearchResult = { order?: Order; patient: Patient };

	@Observer
	@Component({
		components: { GlobalSearchRow, PatientRow, Modal, SearchSVG, Loading }
	})
	export default class GlobalSearch extends Vue {
		@Prop() private patients!: Patient[];
		@Prop() private orders!: OrderWithPatient[];
		private searchResult: SearchResult[] = [];
		private searchTypes = types;
		private searchInput: string = '';
		private searchType: ElementType<typeof types> = types[0];
		private searched = false;

		@Watch('orders')
		private onCriteriaChanged() {
			this.search();
		}

		// eslint-disable-next-line no-useless-escape
		private regex: RegExp = /\<|\>|\:|\“|\"|\/|\\|\||\?|\*/;

		private patientClick(patient) {
			this.$emit('patientClick', patient);
		}

		search() {
			if (this.searchInput.length < 2) {
				return;
			}
			switch (this.searchType) {
				case 'by patient identification':
					this.searchByName();
					break;
				case 'by order number':
					this.searchByOrder();
					break;
				case 'by reference ID':
					this.searchByPatientID();
					break;
				case 'by PO':
					this.searchByZebraId();
					break;
			}

			this.searched = true;
		}

		searchByName() {
			if (this.regex.test(this.searchInput)) return;

			let toFind = this.searchInput.split(/[, ]+/g).map((r) => r.toLowerCase()); // Find all string to find
			toFind = [...new Set(toFind)]; // Remove duplications

			const patients = this.patients.filter((p) => {
				return toFind.every((phrase) => {
					const fullName = p?.user?.firstName?.toLowerCase() + ' ' + p?.user?.lastName?.toLowerCase() || [''];
					return fullName.includes(phrase);
				});
			});

			const orders = this.orders.filter((o) => {
				return toFind.every((phrase) => {
					const fullName = o.order.firstName?.toLowerCase() + ' ' + o.order.lastName?.toLowerCase() || [''];
					return fullName.includes(phrase);
				});
			});

			this.searchResult = [];
			patients.forEach((p) => (p['hasOrder'] = !!p.sessionsStatuses?.find((i) => i?.status?.includes('ORDER'))));

			patients.forEach((p) => {
				if (!p['hasOrder']) {
					this.searchResult.push({ order: undefined, patient: p });
				}
			});

			orders.forEach((p) => {
				this.searchResult.push(p);
			});

			this.searchResult = this.searchResult.sort((a: SearchResult, b: SearchResult) => {
				let timeA = a.order?.createdAt ? new Date(a.order?.createdAt).getTime() / 1000 : a.patient.medicalSolutionData?.lastUpdated;
				let timeB = b.order?.createdAt ? new Date(b.order?.createdAt).getTime() / 1000 : b.patient.medicalSolutionData?.lastUpdated;
				if (timeA && timeB) {
					return +timeB - +timeA;
				}
				return 0;
			});
		}

		searchByField({ fetchFromPatient, fetchFromOrderWihtPatient }: SearchByFieldParams) {
			if (this.regex.test(this.searchInput)) return;

			let toFind = this.searchInput.split(/[, ]+/g).map((r) => r.toLowerCase()); // Find all string to find
			toFind = [...new Set(toFind)]; // Remove duplications

			const patients = this.patients.filter((p) => {
				return toFind.some((r) =>
					fetchFromPatient(p)
						?.replaceAll(/[-]+/g, '')
						?.toLowerCase()
						?.includes(r)
				);
			});

			const orders = this.orders.filter((o) => {
				return toFind.some((r) =>
					fetchFromOrderWihtPatient(o)
						?.replaceAll(/[-]+/g, '')
						?.toLowerCase()
						?.includes(r)
				);
			});

			this.searchResult = [];

			patients.forEach((p) => (p['hasOrder'] = !!p.sessionsStatuses?.find((i) => i?.status?.includes('ORDER'))));

			patients.forEach((p) => {
				if (!p['hasOrder']) {
					this.searchResult.push({ order: undefined, patient: p });
				}
			});

			orders.forEach((p) => {
				this.searchResult.push(p);
			});

			this.searchResult = this.searchResult.slice().sort((a: SearchResult, b: SearchResult) => {
				let timeA = a.order?.createdAt ? new Date(a.order?.createdAt).getTime() / 1000 : a.patient.medicalSolutionData?.lastUpdated;
				let timeB = b.order?.createdAt ? new Date(b.order?.createdAt).getTime() / 1000 : b.patient.medicalSolutionData?.lastUpdated;
				if (timeA && timeB) {
					return +timeB - +timeA;
				}
				return 0;
			});
		}

		searchByOrder() {
			if (this.regex.test(this.searchInput)) return;

			let toFind = this.searchInput.split(/[, ]+/g).map((r) => r.replaceAll(/[-]+/g, '').toLowerCase()); // Find all string to find

			toFind = [...new Set(toFind)]; // Remove duplications
			const orders = this.orders.filter((o) => {
				return toFind.some((r) => {
					return o.order.orderID
						.replaceAll(/[-]+/g, '')
						.toLowerCase()
						.includes(r);
				});
			});

			this.searchResult = [];

			orders.forEach((p) => {
				this.searchResult.push(p);
			});
		}

		searchByPatientID() {
			const fetchFromPatient = (p) => p.medicalSolutionData?.patientID;
			const fetchFromOrderWihtPatient = (op) => op.patient?.medicalSolutionData?.patientID;
			this.searchByField({ fetchFromPatient, fetchFromOrderWihtPatient });
		}

		searchByZebraId() {
			const fetchFromPatient = (p) => p.medicalSolutionData?.prescriptionObj?.uiContextData?.zebraID;
			const fetchFromOrderWihtPatient = (op) =>
				op.patient?.medicalSolutionData?.prescriptionObj?.uiContextData?.zebraID || op.order?.prescriptionObj?.uiContextData?.zebraID;
			this.searchByField({ fetchFromPatient, fetchFromOrderWihtPatient });
		}
	}
